import { MaintenanceService } from './core/services/maintenance.service';
import { Component, OnInit } from '@angular/core';
import { BzgToastService } from 'bz-genome-angular';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { ActivatedRoute } from '@angular/router';
import { IAppState } from 'app/IAppState';
import { IEnvironment } from 'app/environment/models/environment';
import { EnvironmentService } from 'app/environment/services/environment.service';
import { MaintenanceStatus } from './core/services/maintenance-status.enum';
import { WebappsStatus } from './core/services/webapps-status.interface';


@Component({
  selector: 'biz-maintenance',
  templateUrl: './maintenance.component.html',
  styleUrls: ['./maintenance.component.scss']
})
export class MaintenanceComponent implements OnInit {
  status = '';
  statusType = 0;
  fullServiceRestartStatus: MaintenanceStatus = MaintenanceStatus.ACTIVE;
  showLoading = false;
  showLoadingRestart = false;
  isReadyToChange: boolean;
  loadingMessage: string;
  environment: IEnvironment;
  isActiveComponent: boolean;
  responseError: string;

  public tabs: any;
  public selectedTab: number = 0;

  private _tabsSubscription: Subscription;
  private _routeSubscription: Subscription;
  private listTimeOut: any[] = [];
  private intervalProgress: any;

  constructor(private maintenanceService: MaintenanceService,
    private _translateService: TranslateService,
    private _toastService: BzgToastService,
    private route: ActivatedRoute,
    private _store: Store<IAppState>,
    private _environmentService: EnvironmentService
  ) { }

  ngOnInit() {
    this.isActiveComponent = true;
    this.showLoading = true;
    this.showLoadingRestart = true;
    this.isReadyToChange = false;
    this.responseError = '';
    this._initializeEnvironmentConfiguration();
    this._tabsSubscription = this._store
      .select((s) => s.menuOptions)
      .subscribe((menuOptions: any) => {
        this.tabs = menuOptions['MaintenanceWindow'];
      });

    this._routeSubscription = this.route
      .queryParams
      .subscribe(params => {
        if (params && params.tab) {
          this.listTimeOut.push(setTimeout(() => {
            this.selectedTab = parseInt(params.tab, 10);
          }));
        }
      });

    this.getStatus();
    this.checkWebApps();
    this.reviewProgressInterval();
  }

  ngOnDestroy() {
    this.isActiveComponent = false;
    this._tabsSubscription.unsubscribe();
    this.listTimeOut.forEach(timeOut => clearTimeout(timeOut));
    clearInterval(this.intervalProgress);
  }

  private _initializeEnvironmentConfiguration() {
    this._environmentService.getEnvironmentConfiguration().subscribe((environment) => {
      this.environment = environment;
    });
  };

  reviewProgressInterval() {
    this.intervalProgress = setInterval(() => {
      this.getProgress();
    }, 3000);
  }

  getProgress() {
    this.maintenanceService.getProgressMaintenanceWindow().subscribe({
      next: (progress: any) => {
        if (progress === 'InProgress') {
          this.isReadyToChange = false;
        }
        else {
          this.isReadyToChange = true;
          this.showLoading = false;
          clearInterval(this.intervalProgress);
          this.setStatus();
        }
      },
      error: err => {
        //this.showErrorToast(); // TODO Bug351094 Remove toast
        this.showLoading = false;
        this.isReadyToChange = true;
        clearInterval(this.intervalProgress);
      }
    });
  }

  getStatus() {
    this.maintenanceService.getStateMaintenanceWindow().subscribe({
      next: (data: any) => {

        const { State, Mode } = data;
        if (State === 'InProgess') {
          if (['Automatic'].some(x => x === Mode) && this.isActiveComponent) {
            this.status = 'automatic';
            this.listTimeOut.push(setTimeout(() => { this.getStatus() }, 5000));
          } else {
            this.status = 'maintenance';
          }
        } else {
          this.status = 'active';
        }
        this.setStatus();

        this.showLoading = false;
      },
      error: err => {
        this.showErrorToast();
        this.showLoading = false;
      }
    });
  }

  checkWebApps() {
    this.showLoadingRestart = false;
    this.fullServiceRestartStatus = MaintenanceStatus.VERIFYING;
    this.getCheckWebAppsStatus();
  }

  setStatus() {
    if (this.status) {
      if (this.status === 'active')
        this.statusType = 0;
      else if (this.status === 'automatic' || this.status === 'restarting') // TODO: Know when is restarting the server
        this.statusType = 1;
      else
        this.statusType = 2;
    }
  }

  restartWebApps() {
    if (this.fullServiceRestartStatus === MaintenanceStatus.ACTIVE) {
      this.maintenanceService.getRestartWebApps().subscribe((res) => {
        this.fullServiceRestartStatus = MaintenanceStatus.RESTARTING;
        setTimeout(() => {
          this.getCheckWebAppsStatus();
        }, 5000);
      });
    }
  }

  verifyServiceAvailability() {
    this.fullServiceRestartStatus = MaintenanceStatus.VERIFYING;
    this.getCheckWebAppsStatus();
  }

  getCheckWebAppsStatus() {
    this.maintenanceService.getCheckWebApps().subscribe((status: WebappsStatus) => {
      if (status?.ConnectorsStatusOk && status?.EngineStatusOk && status?.SchedulerStatusOk) {
        this.fullServiceRestartStatus = MaintenanceStatus.ACTIVE;
      } else {
        this.fullServiceRestartStatus = MaintenanceStatus.ERROR;
      }
    }, error => {
      this.fullServiceRestartStatus = MaintenanceStatus.ERROR;
    });
  }

  changeStatus() {
    this.showLoading = true;
    if (this.status === 'active') {
      this.turnOnMaintenance();
    } else {
      this.turnOffMaintenance();
    }
  }

  private turnOffMaintenance() {
    this.loadingMessage = 'MAINTENANCE.LOADING_REMOVING';
    this.maintenanceService.turnOffMaintenance().subscribe({
      next: () => {
        this.status = 'active';
        this.isReadyToChange = false;
        this.reviewProgressInterval();
      },
      error: err => {
        this.showErrorToast();
        this.showLoading = false;
        this.isReadyToChange = true;
      }
    });
  }

  private turnOnMaintenance() {
    this.loadingMessage = 'MAINTENANCE.LOADING_APPLYING';
    this.maintenanceService.turnOnMaintenance().subscribe({
      next: () => {
        this.status = 'maintenance';
        this.isReadyToChange = false;
        this.reviewProgressInterval();
      },
      error: err => {
        this.showErrorToast();
        this.showLoading = false;
        this.isReadyToChange = true;
      }
    });
  }

  // GETTERS for dynamic classes and labels -----------------
  get statusText() {
    return (this.status === 'active') ? 'MAINTENANCE.STATUS_TEXT_ACTIVE' : 'MAINTENANCE.STATUS_TEXT_MAINTENANCE';
  }
  get statusLabel() {
    switch (this.status) {
      case '':
        return 'MAINTENANCE.STATUS_LABEL_LOADING';

      case 'active':
        return 'MAINTENANCE.STATUS_LABEL_ACTIVE';

      case 'automatic':
        return 'MAINTENANCE.STATUS_LABEL_AUTOMATIC';

      default:
        return 'MAINTENANCE.STATUS_LABEL_DEFAULT';
    }
  }
  get buttonLabel() {
    return this.status === 'active' ? 'MAINTENANCE.START_MAINTENANCE' : 'MAINTENANCE.START_ENVIRONMENT';
  }
  get statusClass() {
    return 'm-status--' + this.status;
  }
  get showButton() {
    return (this.status === 'active') || (this.status === 'maintenance');
  }

  showErrorToast() {
    this._translateService.get('GLOBAL.GENERIC_ERROR_MSG').subscribe((message: string) => {
      this._toastService.open(
        message,
        {
          type: 'error',
          duration: 0,
          verticalPosition: 'bottom',
          horizontalPosition: 'right'
        });
    });
  }

}
